import { useState } from 'react';
import { useCreateInvoice } from './hooks/useInvoices';
import type { CreateInvoicePayload, InvoiceType } from './types';
import { SOLUTIONS } from '../../shared/constants/solutions';
import Button from '../../shared/components/Button';

const INVOICE_TYPES: { value: InvoiceType; label: string }[] = [
  { value: 'ONE_TIME', label: 'One-time' },
  { value: 'COMMISSION', label: 'Commission' },
  { value: 'RECURRING', label: 'Recurring' },
];

interface LineItemForm {
  description: string;
  quantity: string;
  unitAmount: string;
}

interface Props {
  onClose: () => void;
}

function koboToNaira(kobo: number) {
  return (kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function nairaToKobo(naira: string): number {
  return Math.round(parseFloat(naira) * 100);
}

export function CreateInvoiceModal({ onClose }: Props) {
  const createInvoice = useCreateInvoice();

  const [tenantId, setTenantId] = useState('');
  const [solutionId, setSolutionId] = useState<string>(SOLUTIONS[0].id);
  const [type, setType] = useState<InvoiceType>('ONE_TIME');
  const [currency, setCurrency] = useState('NGN');
  const [dueDate, setDueDate] = useState('');
  const [taxNaira, setTaxNaira] = useState('0');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemForm[]>([
    { description: '', quantity: '1', unitAmount: '' },
  ]);
  const [error, setError] = useState('');

  const addLine = () =>
    setLineItems((prev) => [...prev, { description: '', quantity: '1', unitAmount: '' }]);

  const removeLine = (i: number) =>
    setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const updateLine = (i: number, field: keyof LineItemForm, value: string) =>
    setLineItems((prev) =>
      prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)),
    );

  const computedSubtotal = lineItems.reduce((sum, l) => {
    const qty = parseFloat(l.quantity) || 0;
    const unit = nairaToKobo(l.unitAmount);
    return sum + (isNaN(unit) ? 0 : qty * unit);
  }, 0);

  const taxKobo = nairaToKobo(taxNaira) || 0;
  const totalKobo = computedSubtotal + taxKobo;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!tenantId.trim()) { setError('Tenant ID is required'); return; }
    if (!solutionId.trim()) { setError('Solution ID is required'); return; }
    if (lineItems.some((l) => !l.description.trim() || !l.unitAmount)) {
      setError('All line items must have a description and unit amount');
      return;
    }
    if (totalKobo <= 0) { setError('Total must be greater than zero'); return; }

    const payload: CreateInvoicePayload = {
      tenantId: tenantId.trim(),
      solutionId: solutionId.trim(),
      type,
      currency,
      dueDate: dueDate || undefined,
      notes: notes.trim() || undefined,
      taxAmount: taxKobo,
      totalAmount: totalKobo,
      lineItems: lineItems.map((l) => {
        const qty = parseFloat(l.quantity) || 1;
        const unit = nairaToKobo(l.unitAmount);
        return { description: l.description, quantity: qty, unitAmount: unit, totalAmount: qty * unit };
      }),
    };

    await createInvoice.mutateAsync(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl mx-4 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Issue Invoice to Tenant</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tenant ID *</label>
              <input
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder="e.g. merchant-uuid"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Solution *</label>
              <select
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={solutionId}
                onChange={(e) => setSolutionId(e.target.value)}
              >
                {SOLUTIONS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Invoice Type</label>
              <select
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={type}
                onChange={(e) => setType(e.target.value as InvoiceType)}
              >
                {INVOICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Currency</label>
              <select
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="NGN">NGN</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Due Date</label>
              <input
                type="date"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">Line Items *</label>
              <button type="button" onClick={addLine} className="text-sm text-purple-600 hover:text-purple-800 font-medium">+ Add Item</button>
            </div>
            <div className="space-y-2">
              {lineItems.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    className="col-span-5 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    placeholder="Description"
                    value={line.description}
                    onChange={(e) => updateLine(i, 'description', e.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    className="col-span-2 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    placeholder="Qty"
                    value={line.quantity}
                    onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="col-span-4 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    placeholder="Unit amount (₦)"
                    value={line.unitAmount}
                    onChange={(e) => updateLine(i, 'unitAmount', e.target.value)}
                  />
                  {lineItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="col-span-1 text-red-400 hover:text-red-600 text-lg leading-none font-medium"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Tax Amount (₦)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={taxNaira}
                onChange={(e) => setTaxNaira(e.target.value)}
              />
            </div>
            <div className="flex flex-col justify-end">
              <p className="text-sm text-muted-foreground">Subtotal: <span className="font-medium text-foreground">₦{koboToNaira(computedSubtotal)}</span></p>
              <p className="text-sm font-semibold text-foreground">Total: ₦{koboToNaira(totalKobo)}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
            <textarea
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              rows={2}
              placeholder="Optional note shown on the invoice"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <Button type="submit" disabled={createInvoice.isPending}>
              {createInvoice.isPending ? 'Creating…' : 'Create Invoice'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
